import * as z from "zod";
import { cartContextSchema } from "../cart-context";
import { locationSchema } from "../customers/upsert-customer";

/**
 * Normalizes a v2.2 or v2.3 custom event request body into the canonical v2.3 shape.
 *
 * Only fields that existed in v2.2 have snake_case aliases — new v2.3 fields
 * (value, valueCurrency, uniqueId, and all customer.* sub-fields) are camelCase only.
 *
 * @deprecated snake_case aliases: event_name, event_timestamp, properties,
 * cart_context, and root-level email/phone are accepted for backwards compatibility.
 */
// Strips ignored values from data before OpenAPI validation so null/0/"" don't
// fail the schema check. normalizeEventProperties in the handler does the same
// pass again (plus ISO date coercion) — keep the two in sync if either changes.
function stripIgnoredDataValues(
  data: unknown,
): Record<string, unknown> | undefined {
  if (typeof data !== "object" || data === null) {
    return undefined;
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (key === "$extra") {
      result[key] = value;
      continue;
    }
    if (value === 0 || value === null || value === undefined || value === "") {
      continue;
    }
    result[key] = value;
  }
  return result;
}

export function normalizeCustomEventRequest(body: unknown): unknown {
  if (typeof body !== "object" || body === null) {
    return body;
  }
  const b = body as Record<string, unknown>;
  const existingCustomer = (b.customer as Record<string, unknown>) ?? {};
  const rawData = b.data ?? b.properties;
  return {
    // v2.2 aliases — snake_case still accepted for backwards compat
    eventName: b.eventName ?? b.event_name, // @deprecated event_name
    eventTimestamp: b.eventTimestamp ?? b.event_timestamp, // @deprecated event_timestamp
    data: stripIgnoredDataValues(rawData), // @deprecated properties
    cartContext: b.cartContext ?? b.cart_context, // @deprecated cart_context

    // v2.3 only — no snake_case aliases
    value: b.value,
    valueCurrency: b.valueCurrency,
    uniqueId: b.uniqueId,

    customer: {
      ...existingCustomer,
      // v2.2 aliases: email/phone were at root level
      email: existingCustomer.email ?? b.email, // @deprecated root email
      phoneNumber: existingCustomer.phoneNumber ?? b.phone, // @deprecated root phone
      // id, firstName, lastName, location, customFields: v2.3 only
    },
  };
}

const customEventCustomerSchema = z
  .object({
    id: z.string().optional(),
    email: z.string().email().optional(),
    phoneNumber: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    location: locationSchema.optional(),
    customFields: z
      .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
      .optional(),
  })
  .refine((c) => !!c.id || !!c.email || !!c.phoneNumber, {
    message: "At least one of id, email, or phoneNumber is required",
  });

export type CustomEventCustomer = z.infer<typeof customEventCustomerSchema>;

/** Canonical list of scalar event property types that support segmentation. */
export const EVENT_PROPERTY_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
] as const;

export type EventPropertyType = (typeof EVENT_PROPERTY_TYPES)[number];

/**
 * Valid property name pattern for segmentable custom event data keys.
 * Accepts letters, numbers, spaces, hyphens, and underscores.
 */
export const PROPERTY_NAME_PATTERN = /^[\w -]+$/;

export function isPropertyNameValid(name: string): boolean {
  return name.length > 0 && PROPERTY_NAME_PATTERN.test(name);
}

/** Maximum number of top-level keys in the data object. */
export const MAX_DATA_PROPERTIES = 400;

/** Maximum length of any string value in the data object (100 KB). */
export const MAX_STRING_BYTES = 100 * 1024;

/** Maximum number of items in any array value in the data object. */
export const MAX_ARRAY_ITEMS = 4000;

/**
 * Validates data property limits in a single pass:
 * - Max 400 top-level keys
 * - Max 100 KB per string value
 * - Max 4,000 items per array value
 */
export function validateDataLimits(
  d: Record<string, unknown> | null | undefined,
): { valid: true } | { valid: false; message: string } {
  if (d == null) {
    return { valid: true };
  }
  const keys = Object.keys(d);
  if (keys.length > MAX_DATA_PROPERTIES) {
    return {
      valid: false,
      message: `data must not exceed ${MAX_DATA_PROPERTIES} properties`,
    };
  }
  for (const key of keys) {
    if (key !== "$extra" && !PROPERTY_NAME_PATTERN.test(key)) {
      return {
        valid: false,
        message: `data property name "${key}" is invalid — only letters, numbers, spaces, hyphens, and underscores are allowed`,
      };
    }
  }
  for (const value of Object.values(d)) {
    if (
      typeof value === "string" &&
      Buffer.byteLength(value, "utf8") > MAX_STRING_BYTES
    ) {
      return {
        valid: false,
        message: "data string values must not exceed 100 KB",
      };
    }
    if (Array.isArray(value) && value.length > MAX_ARRAY_ITEMS) {
      return {
        valid: false,
        message: `data arrays must not exceed ${MAX_ARRAY_ITEMS} items`,
      };
    }
  }
  return { valid: true };
}

/**
 * Schema for the Create Custom Event Public API request body (v2.3).
 *
 * Call normalizeCustomEventRequest() on the raw request body before parsing
 * to support v2.2 clients sending deprecated snake_case field names.
 *
 * Data limits (matching Klaviyo):
 * - Max 400 properties in data
 * - Max 5 MB total payload (enforced at the HTTP layer)
 * - Max 100 KB per string value
 * - Max 4,000 items per array value
 * - uniqueId: 1–255 characters
 */
const customEventRequestSchema_ = z.object({
  eventName: z.string().min(1).max(100),
  customer: customEventCustomerSchema,
  /** ISO 8601 datetime. Defaults to server ingest time if omitted. */
  eventTimestamp: z.string().datetime({ offset: true }).optional(),
  /** Monetary or conversion value associated with the event. */
  value: z.number().optional(),
  /** ISO 4217 currency code, e.g. "USD". */
  valueCurrency: z.string().optional(),
  /**
   * Custom event properties. Flat top-level keys are segmentable; nest
   * non-segmentable data (arrays, objects) under the $extra key.
   *
   * Limits: max 400 properties, max 100 KB per string, max 4,000 array items.
   */
  data: z
    .record(z.string(), z.unknown())
    .optional()
    .superRefine((d: Record<string, unknown> | undefined, ctx) => {
      const result = validateDataLimits(d);
      if (!result.valid) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message });
      }
    }),
  /** Hidden from public docs — powers product recommendations (MKTG-1798). */
  cartContext: cartContextSchema.nullish(),
  /** Merchant-controlled deduplication key. 1–255 characters. */
  uniqueId: z.string().min(1).max(255).optional(),
});

export const customEventRequestSchema: z.ZodType<CustomEventRequest> =
  customEventRequestSchema_;

export interface CustomEventRequest extends z.infer<
  typeof customEventRequestSchema_
> {}
